// src/utils/reportImages.js — every picture a PDF report needs, fetched and
// prepared BEFORE the PDF is built.
// ---------------------------------------------------------------------------
// v0.215.0. Brett: "download pdf doesnt give me any images" — every photo box
// on every card empty, and no plan thumbnails.
//
// WHAT WAS WRONG. The report handed jsPDF WEB ADDRESSES (signed blob links) and
// let jsPDF fetch them. jsPDF does that badly, and fails SILENTLY:
//
//   • it runs unescape() on the address first. A signed link's signature is
//     percent-encoded, and unescaping turns "%2B" into "+", which storage reads
//     as a space: the signature no longer matches and storage answers 403.
//     Measured on the Midland photos: two of four links refused that way.
//   • it fetches with a synchronous request that storage's cross-site rules
//     must allow; they allow droppin.insitebg.com.au only, so from any other
//     address the app runs at, every fetch fails.
//   • and when the fetch fails it does NOT throw. It embeds the ADDRESS TEXT
//     as if it were the JPEG. The PDF gets a bordered box with nothing in it,
//     and the "couldn't render" note never shows, because nothing failed.
//
//   The plan thumbnail had a second cause: a drawing whose picture was never
//   made (a PDF plan with no raster) was handed over as the PDF itself, which
//   jsPDF cannot draw.
//
// WHY IT IS SHAPED LIKE THIS. The PDF builder is synchronous and knows nothing
// about the network, so the network must be finished with before it starts.
// This module does all of it, asynchronously and visibly: fetch, check the
// bytes really are an image (not an error page), decode, downscale to what
// print needs, and hand back data the builder can embed — or a REASON, per
// photo, that the report states instead of a blank box. The builder is then
// given nothing it could fetch (see planImage / photoImage in the template).
//
// Everything that touches a browser API is injected, so the decisions here are
// tested in node (reportImages.test.mjs).

export const PHOTO_MAX_EDGE = 1600;   // a 40 mm print cell needs far less; a full-page photo about this
export const PLAN_MAX_EDGE = 3200;    // grout-line pins on an A4 plan page stay legible
export const LOGO_MAX_EDGE = 600;

/** What the leading bytes say a file is. Pure. */
export function sniffImage(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'png';
  if (b.length >= 4 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) return 'pdf';
  if (b.length >= 12 && String.fromCharCode(...b.slice(0, 4)) === 'RIFF' && String.fromCharCode(...b.slice(8, 12)) === 'WEBP') return 'webp';
  if (b.length >= 6 && String.fromCharCode(...b.slice(0, 4)) === 'GIF8') return 'gif';
  if (b.length >= 12 && String.fromCharCode(...b.slice(4, 8)) === 'ftyp') {
    const brand = String.fromCharCode(...b.slice(8, 12));
    if (/^(hei[cxms]|hev[cxms]|mif1|msf1)$/.test(brand)) return 'heic';
  }
  return null;
}

/** An embeddable image as the PDF builder accepts it: a JPEG or PNG data URL. Pure. */
export function isEmbeddable(s) {
  return typeof s === 'string' && /^data:image\/(jpeg|jpg|png);base64,/i.test(s);
}

const reasonFor = (e) => {
  if (!e) return 'unknown error';
  if (e.reason) return e.reason;
  // fetch() rejects with a bare TypeError for no connection AND for storage
  // refusing this site; the browser will not say which, so neither do we.
  if (e.name === 'TypeError') return 'could not be downloaded (no connection, or storage refused this address)';
  return e.message || String(e);
};
const fail = (reason) => Object.assign(new Error(reason), { reason });

/**
 * The browser implementations. Kept together so a test can replace all of them.
 */
export function browserDeps() {
  return {
    fetchImpl: (url) => fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store' }),
    async toJpeg(blob, maxEdge) {
      // from-image: the camera's EXIF rotation is applied, so a portrait photo
      // prints portrait. Canvas re-encode also strips the EXIF itself.
      const bmp = await createImageBitmap(blob, { imageOrientation: 'from-image' });
      try {
        const k = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
        const w = Math.max(1, Math.round(bmp.width * k)), h = Math.max(1, Math.round(bmp.height * k));
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        const g = cv.getContext('2d');
        g.fillStyle = '#ffffff'; g.fillRect(0, 0, w, h);   // a transparent PNG must not print black
        g.drawImage(bmp, 0, 0, w, h);
        return { dataUrl: cv.toDataURL('image/jpeg', 0.88), width: w, height: h };
      } finally {
        if (bmp.close) bmp.close();
      }
    },
    async pdfToImage(blob, page) {
      const { pdfPageToPng } = await import('./pdfRaster.js');
      const png = await pdfPageToPng(blob, { page: page || 1, scale: 2 });
      return fetch(png).then((r) => r.blob());
    },
    async heicToJpeg(blob) {
      const { toBrowserImage } = await import('./heicPhotos.js');
      return toBrowserImage(new File([blob], 'photo.heic', { type: 'image/heic' }));
    },
  };
}

/** One address to embeddable image data, or a thrown { reason }. */
async function loadOne(url, maxEdge, deps, opts = {}) {
  if (!url) throw fail('no address');
  if (isEmbeddable(url)) return { dataUrl: url, width: null, height: null };
  let res;
  try { res = await deps.fetchImpl(url); } catch (e) { throw fail(reasonFor(e)); }
  if (!res || !res.ok) {
    const st = res ? res.status : '?';
    throw fail(st === 403 ? 'storage refused the link (403 — expired or not signed)'
      : st === 404 ? 'the file is missing from storage (404)'
      : `storage answered HTTP ${st}`);
  }
  let blob = await res.blob();
  const head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
  let kind = sniffImage(head);
  if (kind === 'pdf') {
    if (!opts.allowPdf) throw fail('is a PDF, not a picture');
    blob = await deps.pdfToImage(blob, opts.page);
    kind = 'png';
  } else if (kind === 'heic') {
    blob = await deps.heicToJpeg(blob);
    kind = 'jpeg';
  }
  if (!kind) throw fail('the download was not an image');
  return deps.toJpeg(blob, maxEdge);
}

/** Run fn over items, `limit` at a time, keeping order. */
async function pool(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      // eslint-disable-next-line no-await-in-loop
      out[i] = await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

/**
 * Fetch and prepare every image the report will draw.
 *
 * Returns NEW arrays (inputs untouched):
 *   drawings[i].pdfImage      { dataUrl, width, height }  or  .pdfImageError  (reason)
 *   defects[i].photos[j].pdfImage / .pdfImageError
 *   logo                      data URL or null
 *   problems                  one line per image that could not be prepared
 *
 * A photo tries its display copy first and falls back to the original, so a
 * missing or refused mid-size copy costs bandwidth, not the photo.
 */
export async function loadReportImages({ drawings = [], defects = [], logoUrl = null } = {}, deps = browserDeps(), opts = {}) {
  const limit = opts.concurrency || 4;
  const onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : () => {};
  const problems = [];
  const cache = new Map();
  const cached = (key, make) => {
    if (!cache.has(key)) cache.set(key, make());
    return cache.get(key);
  };

  const jobs = [];
  defects.forEach((d, di) => (Array.isArray(d && d.photos) ? d.photos : []).forEach((p, pi) => jobs.push({ kind: 'photo', di, pi, p })));
  drawings.forEach((dr, i) => jobs.push({ kind: 'plan', i, dr }));
  const total = jobs.length + (logoUrl ? 1 : 0);
  let done = 0;
  const tick = () => { done += 1; onProgress({ done, total }); };

  const photoOut = new Map();
  const planOut = new Map();
  await pool(jobs, limit, async (job) => {
    if (job.kind === 'photo') {
      const p = job.p || {};
      const tries = [p.displayUrl, p.url].filter((u, k, a) => u && a.indexOf(u) === k);
      let result = null, lastReason = 'no address';
      for (const u of tries) {
        try {
          // eslint-disable-next-line no-await-in-loop
          result = await cached(`p|${u}`, () => loadOne(u, PHOTO_MAX_EDGE, deps));
          break;
        } catch (e) { lastReason = reasonFor(e); }
      }
      photoOut.set(`${job.di}|${job.pi}`, result ? { pdfImage: result } : { pdfImageError: lastReason });
      if (!result) {
        const d = defects[job.di] || {};
        problems.push(`Defect ${d.defectNumber ?? d.id ?? '?'}: a photo ${lastReason}`);
      }
    } else {
      const dr = job.dr || {};
      const tries = [dr.rasterUrl, dr.sourceUrl].filter((u, k, a) => u && a.indexOf(u) === k);
      let result = null, lastReason = 'no plan image';
      for (const u of tries) {
        try {
          // eslint-disable-next-line no-await-in-loop
          result = await cached(`d|${u}|${dr.pageNumber || 1}`, () => loadOne(u, PLAN_MAX_EDGE, deps, { allowPdf: true, page: dr.pageNumber || 1 }));
          break;
        } catch (e) { lastReason = reasonFor(e); }
      }
      planOut.set(job.i, result ? { pdfImage: result } : { pdfImageError: lastReason });
      if (!result && tries.length) problems.push(`Plan "${dr.name || dr.title || dr.id}": ${lastReason}`);
    }
    tick();
  });

  let logo = null;
  if (logoUrl) {
    try { logo = (await loadOne(logoUrl, LOGO_MAX_EDGE, deps)).dataUrl; } catch (e) { problems.push(`Logo: ${reasonFor(e)}`); }
    tick();
  }

  return {
    drawings: drawings.map((dr, i) => ({ ...dr, ...(planOut.get(i) || {}) })),
    defects: defects.map((d, di) => (Array.isArray(d && d.photos)
      ? { ...d, photos: d.photos.map((p, pi) => ({ ...p, ...(photoOut.get(`${di}|${pi}`) || {}) })) }
      : d)),
    logo,
    problems,
  };
}
